import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getFromStorage, removeFromStorage, saveToStorage } from "utils/localStorage";

interface PrivateRoutesProps {
  children: React.ReactNode;
}

const PrivateRoutes: React.FC<PrivateRoutesProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = useState(() => getFromStorage("token"));
  const [secretKey, setSecretKey] = useState(() => getFromStorage("secret-key"));
  const [expireDate, setExpireDate] = useState(() => getFromStorage("expire-token-date"));
  const invoicePermission = getFromStorage("invoice");
  const savedPath = getFromStorage("path");

  const formatDate = useCallback((date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }, []);

  const handleStorageChange = useCallback(() => {
    setToken(getFromStorage("token"));
    setSecretKey(getFromStorage("secret-key"));
    setExpireDate(getFromStorage("expire-token-date"));
  }, []);

  // Save invoice path if accessed directly
  useEffect(() => {
    if (location.pathname === "/dashboard/invoice") {
      saveToStorage("path", location.pathname);
    }
    // eslint-disable-next-line
  }, []);

  // Check token expiry
  useEffect(() => {
    if (!expireDate) return;

    const now = formatDate(new Date());
    if (expireDate <= now) {
      sessionStorage.clear();
      removeFromStorage("token");
      navigate("/");
    }
  }, [expireDate, formatDate, navigate]);

  // Auth logic
  useEffect(() => {
    if (!token || !secretKey) {
      navigate("/");
      return;
    }
 
    if (location.pathname.startsWith("/dashboard/") && !token) {
      navigate("/");
      return;
    }
   
    if (token && invoicePermission === "true" && savedPath === "/dashboard/invoice") {
      removeFromStorage("path");
      navigate("/dashboard/invoice");
    }

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [
    token,
    secretKey,
    invoicePermission,
    savedPath,
    location.pathname,
    navigate,
    handleStorageChange,
  ]);

  if (!token || !secretKey) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoutes;